import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  ChevronDown, 
  ChevronUp, 
  HelpCircle, 
  Search,
  Shield,
  Server,
  Network,
  Cloud,
  Users,
  Phone,
  Mail,
  MessageCircle
} from 'lucide-react';

const FAQ = () => {
  const [openItems, setOpenItems] = useState(new Set([0])); // First item open by default
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'Alle Fragen', icon: HelpCircle },
    { id: 'general', name: 'Allgemein', icon: Users },
    { id: 'security', name: 'IT-Sicherheit', icon: Shield },
    { id: 'infrastructure', name: 'IT-Infrastruktur', icon: Server },
    { id: 'network', name: 'Netzwerk', icon: Network },
    { id: 'cloud', name: 'Cloud Services', icon: Cloud },
    { id: 'support', name: 'Support & Service', icon: Phone }
  ];

  const faqData = [
    // Allgemeine Fragen
    {
      id: 1,
      category: 'general',
      question: 'Welche IT-Services bietet NetSyTe an?',
      answer: 'NetSyTe bietet umfassende IT-Dienstleistungen für Unternehmen in Deutschland an, einschließlich IT-Infrastruktur-Management, Cybersicherheit, Netzwerkmanagement und strategische IT-Beratung. Unsere Services reichen von der Planung und Implementierung bis hin zur laufenden Wartung und dem Support Ihrer IT-Systeme.',
      keywords: ['IT-Services', 'IT-Dienstleistungen', 'IT-Support', 'Deutschland']
    },
    {
      id: 2,
      category: 'general',
      question: 'Für welche Unternehmensgrößen sind Ihre Services geeignet?',
      answer: 'Unsere IT-Services sind für Unternehmen aller Größen geeignet - von kleinen Betrieben mit 5-10 Arbeitsplätzen bis hin zu mittelständischen Unternehmen mit über 200 Mitarbeitern. Wir passen unsere Lösungen individuell an die Bedürfnisse und das Budget Ihres Unternehmens an.',
      keywords: ['KMU', 'kleine Unternehmen', 'Mittelstand', 'Unternehmensgrößen']
    },
    {
      id: 3,
      category: 'general',
      question: 'In welchen Regionen ist NetSyTe tätig?',
      answer: 'NetSyTe bietet IT-Services deutschlandweit an. Unser Hauptfokus liegt auf Unternehmen in Deutschland, wobei wir sowohl Remote-Services als auch Vor-Ort-Support anbieten. Für dringende Fälle stehen wir auch außerhalb der regulären Geschäftszeiten zur Verfügung.',
      keywords: ['Deutschland', 'Remote-Support', 'Vor-Ort-Service', 'deutschlandweit']
    },

    // IT-Sicherheit
    {
      id: 4,
      category: 'security',
      question: 'Wie schützen Sie mein Unternehmen vor Cyberbedrohungen?',
      answer: 'Wir implementieren mehrschichtige Sicherheitslösungen: Next-Generation Firewalls, Endpoint Protection, E-Mail-Sicherheit, regelmäßige Sicherheitsupdates und Mitarbeiterschulungen. Zusätzlich führen wir kontinuierliche Überwachung und regelmäßige Sicherheitsaudits durch, um Ihr Unternehmen vor aktuellen Cyberbedrohungen zu schützen.',
      keywords: ['Cybersecurity', 'Firewall', 'Endpoint Protection', 'Sicherheitsaudit']
    },
    {
      id: 5,
      category: 'security',
      question: 'Was ist ein Cyber-Risiko-Check und brauche ich das?',
      answer: 'Ein Cyber-Risiko-Check ist eine umfassende Bewertung Ihrer aktuellen IT-Sicherheitslage. Wir analysieren Ihre Systeme, identifizieren Schwachstellen und geben konkrete Handlungsempfehlungen. Dies ist besonders wichtig für Unternehmen, die noch keine professionelle IT-Sicherheitsstrategie haben oder ihre bestehenden Maßnahmen überprüfen möchten.',
      keywords: ['Cyber-Risiko-Check', 'Sicherheitsbewertung', 'Schwachstellen-Analyse']
    },
    {
      id: 6,
      category: 'security',
      question: 'Wie oft sollten Sicherheitsupdates durchgeführt werden?',
      answer: 'Kritische Sicherheitsupdates sollten sofort installiert werden, reguläre Updates mindestens monatlich. Wir empfehlen ein proaktives Update-Management mit automatisierten Prozessen für unkritische Updates und kontrollierte Rollouts für kritische Systeme. Unsere Wartungspakete beinhalten die komplette Update-Verwaltung.',
      keywords: ['Sicherheitsupdates', 'Patch-Management', 'Update-Strategie']
    },

    // IT-Infrastruktur
    {
      id: 7,
      category: 'infrastructure',
      question: 'Wann sollte ich meine IT-Infrastruktur modernisieren?',
      answer: 'Eine Modernisierung ist empfehlenswert, wenn Ihre Systeme häufig ausfallen, langsam arbeiten, nicht mehr vom Hersteller unterstützt werden oder Sicherheitsrisiken darstellen. Auch bei Unternehmenswachstum oder neuen Compliance-Anforderungen ist eine Überprüfung sinnvoll. Wir bieten kostenlose IT-Assessments zur Bewertung Ihrer aktuellen Infrastruktur an.',
      keywords: ['IT-Modernisierung', 'IT-Assessment', 'Infrastruktur-Upgrade']
    },
    {
      id: 8,
      category: 'infrastructure',
      question: 'Welche Backup-Strategien empfehlen Sie?',
      answer: 'Wir empfehlen die 3-2-1-Regel: 3 Kopien Ihrer Daten, auf 2 verschiedenen Medientypen, mit 1 Kopie extern (z.B. Cloud). Moderne Backup-Lösungen sollten automatisiert, verschlüsselt und regelmäßig getestet werden. Je nach Unternehmensgröße bieten wir lokale Backup-Systeme, Cloud-Backups oder hybride Lösungen an.',
      keywords: ['Backup-Strategie', '3-2-1-Regel', 'Cloud-Backup', 'Datensicherung']
    },

    // Netzwerk
    {
      id: 9,
      category: 'network',
      question: 'Wie verbessere ich die WLAN-Abdeckung in meinem Büro?',
      answer: 'Eine professionelle WLAN-Ausleuchtung beginnt mit einer Standortanalyse. Wir planen das optimale Access Point-Layout, berücksichtigen Störquellen und implementieren Enterprise-WLAN mit zentralem Management. Modern WiFi 6/6E-Technologie bietet bessere Performance und kann mehr Geräte gleichzeitig verwalten.',
      keywords: ['WLAN-Optimierung', 'WiFi 6', 'Access Points', 'WLAN-Planung']
    },
    {
      id: 10,
      category: 'network',
      question: 'Was ist der Unterschied zwischen LAN und WLAN?',
      answer: 'LAN (Local Area Network) ist ein kabelgebundenes Netzwerk mit stabilen, schnellen Verbindungen über Ethernet-Kabel. WLAN (Wireless LAN) bietet kabellose Verbindungen über Funk. LAN ist sicherer und stabiler, WLAN bietet mehr Flexibilität. In modernen Büroumgebungen kombinieren wir beide Technologien optimal.',
      keywords: ['LAN', 'WLAN', 'Ethernet', 'Netzwerk-Grundlagen']
    },

    // Cloud Services
    {
      id: 11,
      category: 'cloud',
      question: 'Sollte mein Unternehmen in die Cloud wechseln?',
      answer: 'Cloud-Migration bietet viele Vorteile: Skalierbarkeit, Kosteneffizienz, automatische Updates und bessere Zusammenarbeit. Ob es für Ihr Unternehmen sinnvoll ist, hängt von Ihren spezifischen Anforderungen, Compliance-Vorgaben und dem Budget ab. Wir führen eine individuelle Cloud-Readiness-Bewertung durch und planen die Migration schrittweise.',
      keywords: ['Cloud-Migration', 'Cloud-Vorteile', 'Cloud-Readiness', 'Digitale Transformation']
    },
    {
      id: 12,
      category: 'cloud',
      question: 'Ist die Cloud sicher für sensible Unternehmensdaten?',
      answer: 'Moderne Cloud-Anbieter bieten höchste Sicherheitsstandards - oft besser als lokale Lösungen kleiner Unternehmen. Wichtig sind: Verschlüsselung, Zugriffskontrolle, DSGVO-Compliance und die Wahl seriöser Anbieter mit deutschen/EU-Rechenzentren. Wir helfen bei der sicheren Cloud-Konfiguration und Compliance-Einhaltung.',
      keywords: ['Cloud-Sicherheit', 'DSGVO', 'Datenschutz', 'Verschlüsselung']
    },

    // Support & Service
    {
      id: 13,
      category: 'support',
      question: 'Wie schnell reagieren Sie bei IT-Problemen?',
      answer: 'Unsere Reaktionszeiten richten sich nach der Dringlichkeit: Kritische Systemausfälle bearbeiten wir innerhalb von 2 Stunden, bei Standard-Anfragen reagieren wir binnen 4 Stunden. Wartungsarbeiten werden innerhalb von 24 Stunden geplant. Für Notfälle außerhalb der Geschäftszeiten haben wir einen Bereitschaftsdienst.',
      keywords: ['Reaktionszeiten', 'IT-Notfall', 'Support-Service', 'SLA']
    },
    {
      id: 14,
      category: 'support',
      question: 'Welche Support-Kanäle bieten Sie an?',
      answer: 'Wir bieten mehrere Support-Kanäle: E-Mail-Support, Remote-Zugriff für direkte Problemlösung, Vor-Ort-Service bei komplexen Hardware-Problemen und ein Online-Portal für Ticket-Verwaltung. Unsere Techniker sind deutschsprachig und auf moderne IT-Umgebungen spezialisiert.',
      keywords: ['Support-Kanäle', 'Remote-Support', 'Vor-Ort-Service', 'Ticket-System']
    },
    {
      id: 15,
      category: 'support',
      question: 'Was kostet professioneller IT-Support?',
      answer: 'Unsere IT-Support-Kosten richten sich nach Ihrem Bedarf: On-Demand-Service ab 95€/Stunde, Basis-Wartungspakete ab 150€/Monat für kleine Unternehmen, Professional-Pakete ab 299€/Monat für umfassende Betreuung. Alle Pakete sind transparent kalkuliert ohne versteckte Kosten.',
      keywords: ['IT-Support-Kosten', 'Wartungsvertrag', 'IT-Service-Preise', 'Transparente Preise']
    }
  ];

  const toggleItem = (index) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(index)) {
      newOpenItems.delete(index);
    } else {
      newOpenItems.add(index);
    }
    setOpenItems(newOpenItems);
  };

  const filteredFAQs = faqData.filter(faq => {
    const matchesCategory = selectedCategory === 'all' || faq.category === selectedCategory;
    const matchesSearch = !searchTerm || 
      faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.keywords.some(keyword => keyword.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen py-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center space-y-4 mb-16">
          <Badge className="bg-purple-100 text-purple-800">
            <HelpCircle className="mr-1 h-3 w-3" />
            Häufige Fragen
          </Badge>
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900">
            FAQ - IT-Services & Support
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Antworten auf die häufigsten Fragen zu unseren IT-Dienstleistungen, 
            Sicherheitslösungen und Support-Services für Unternehmen in Deutschland.
          </p>
        </div>

        {/* Search Bar */}
        <div className="max-w-2xl mx-auto mb-12">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Suchen Sie nach IT-Themen, Sicherheit, Support..."
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) => (
            <Button
              key={category.id}
              variant={selectedCategory === category.id ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedCategory(category.id)}
              className="mb-2"
            >
              <category.icon className="mr-2 h-3 w-3" />
              {category.name}
            </Button>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* FAQ Content */}
          <div className="lg:col-span-2">
            {filteredFAQs.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <HelpCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Keine Ergebnisse gefunden
                  </h3>
                  <p className="text-gray-600">
                    Probieren Sie andere Suchbegriffe oder wählen Sie eine andere Kategorie.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {filteredFAQs.map((faq, index) => (
                  <Card key={faq.id} className="hover:shadow-lg transition-shadow">
                    <CardHeader 
                      className="cursor-pointer"
                      onClick={() => toggleItem(index)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="outline" className="text-xs">
                              {categories.find(c => c.id === faq.category)?.name}
                            </Badge>
                          </div>
                          <CardTitle className="text-lg text-left hover:text-blue-600 transition-colors">
                            {faq.question}
                          </CardTitle>
                        </div>
                        {openItems.has(index) ? (
                          <ChevronUp className="h-5 w-5 text-gray-500" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-gray-500" />
                        )}
                      </div>
                    </CardHeader>
                    
                    {openItems.has(index) && (
                      <CardContent>
                        <div className="text-gray-700 leading-relaxed">
                          {faq.answer}
                        </div>
                        {faq.keywords && (
                          <div className="mt-4 pt-4 border-t">
                            <div className="flex flex-wrap gap-1">
                              {faq.keywords.slice(0, 4).map((keyword, idx) => (
                                <Badge key={idx} variant="secondary" className="text-xs">
                                  {keyword}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Contact */}
            <Card className="bg-blue-600 text-white">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold mb-4">
                  Frage nicht dabei?
                </h3>
                <p className="text-blue-100 mb-4 text-sm">
                  Kontaktieren Sie unsere IT-Experten für individuelle Beratung und maßgeschneiderte Lösungen.
                </p>
                <div className="space-y-3">
                  <Button size="sm" variant="secondary" className="w-full" asChild>
                    <Link to="/contact">
                      <Mail className="mr-2 h-3 w-3" />
                      E-Mail senden
                    </Link>
                  </Button>
                  <Button size="sm" variant="outline" className="w-full border-white text-white hover:bg-white hover:text-blue-600" asChild>
                    <Link to="/cyber-risiko-check">
                      <Shield className="mr-2 h-3 w-3" />
                      Cyber-Check starten
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Popular Categories */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Beliebte Kategorien</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {categories.slice(1).map((category) => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`w-full text-left p-3 rounded-lg transition-colors flex items-center ${
                        selectedCategory === category.id 
                          ? 'bg-blue-50 text-blue-700 border border-blue-200' 
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <category.icon className="h-4 w-4 mr-2" />
                      <span className="text-sm font-medium">{category.name}</span>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Related Services */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Unsere Services</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <Link to="/services" className="block text-sm text-gray-600 hover:text-blue-600 transition-colors">
                    → IT-Infrastruktur & Server
                  </Link>
                  <Link to="/services" className="block text-sm text-gray-600 hover:text-blue-600 transition-colors">
                    → Cybersicherheit & Firewalls
                  </Link>
                  <Link to="/services" className="block text-sm text-gray-600 hover:text-blue-600 transition-colors">
                    → Netzwerk & WLAN-Lösungen
                  </Link>
                  <Link to="/service-models" className="block text-sm text-gray-600 hover:text-blue-600 transition-colors">
                    → Service-Pakete & Preise
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center bg-gray-50 rounded-2xl p-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Weitere Fragen zu unseren IT-Services?
          </h3>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Unsere IT-Experten beraten Sie gerne persönlich zu allen Aspekten 
            der IT-Sicherheit, Netzwerkoptimierung und digitalen Transformation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" asChild>
              <Link to="/contact">
                <MessageCircle className="mr-2 h-4 w-4" />
                Kostenlose Beratung anfragen
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to="/references">
                <HelpCircle className="mr-2 h-4 w-4" />
                Referenzen ansehen
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQ;